import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "../api";
import { Spinner } from "../components/ui";
import type { PublicWalletTransfer } from "../wallet-types";
import { isDurableWalletReview, queryableTransfer, walletSettlementSummary, walletTransferLabel } from "./wallet-intent";
import type { Transaction } from "../types";
import { MobilePrimaryButton, PageHeader, RecordEmpty } from "./shared";
import type { MobilePageProps } from "./types";
import "./record-pages.css";
import { BetRecordList } from "./BetRecordList";
import { GameStatistics } from "./GameStatistics";

type RecordRoute = "funds" | "game-records";
export type RecordPeriod = "today" | "yesterday" | "month" | "last-month";
type FundsTab = "deposit" | "withdraw" | "transfer" | "detail";
type GameTab = "profit" | "rebate" | "games";
type RecordTab = FundsTab | GameTab;

const periods: { value: RecordPeriod; label: string }[] = [
  { value: "today", label: "今天" },
  { value: "yesterday", label: "昨日" },
  { value: "month", label: "本月" },
  { value: "last-month", label: "上月" },
];
const fundsTabs: { value: FundsTab; label: string }[] = [
  { value: "deposit", label: "充值记录" },
  { value: "withdraw", label: "提现记录" },
  { value: "transfer", label: "转账记录" },
  { value: "detail", label: "账户明细" },
];
const gameTabs: { value: GameTab; label: string }[] = [
  { value: "profit", label: "场馆盈亏" },
  { value: "games", label: "游戏注单" },
  { value: "rebate", label: "实时返水" },
];
const currency = new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" });
const calendar = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" });
const dateTime = new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
const day = 24 * 60 * 60 * 1000;
const pageSize = 30;

function TransferSettlementDetails({ transfer }: { transfer: PublicWalletTransfer }) {
  const settlement = walletSettlementSummary(transfer);
  if (!settlement) return null;
  return <dl className="mobile-record-settlement" aria-label="部分到账明细">
    <div><dt>申请金额</dt><dd>{currency.format(settlement.requestedAmountCents / 100)}</dd></div>
    <div><dt>{settlement.actualLabel}</dt><dd>{currency.format(settlement.actualAmountCents / 100)}</dd></div>
    <div><dt>{settlement.differenceLabel}</dt><dd>{currency.format(settlement.differenceAmountCents / 100)}</dd></div>
  </dl>;
}

/** Period boundaries follow the member site's Shanghai calendar, independent of the device timezone. */
export function recordPeriodRange(period: RecordPeriod, now = new Date()): { start: number; end: number; label: string } {
  const parts = calendar.formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  const year = value("year");
  const month = value("month") - 1;
  const date = value("day");
  const midnight = (y: number, m: number, d: number) => Date.UTC(y, m, d) - 8 * 60 * 60 * 1000;
  const today = midnight(year, month, date);
  let start = today;
  let end = today + day;
  if (period === "yesterday") { start -= day; end -= day; }
  if (period === "month") { start = midnight(year, month, 1); end = midnight(year, month + 1, 1); }
  if (period === "last-month") { start = midnight(year, month - 1, 1); end = midnight(year, month, 1); }
  const shortDate = (timestamp: number) => {
    const pieces = calendar.formatToParts(timestamp);
    return `${pieces.find((part) => part.type === "month")?.value}-${pieces.find((part) => part.type === "day")?.value}`;
  };
  return { start, end, label: period === "month" || period === "last-month" ? `${shortDate(start)} 至 ${shortDate(end - 1)}` : shortDate(start) };
}

export function filterMemberRecords(transactions: Transaction[], userId: string, tab: RecordTab, period: RecordPeriod, now = new Date()): Transaction[] {
  if (tab !== "deposit" && tab !== "withdraw" && tab !== "detail") return [];
  const { start, end } = recordPeriodRange(period, now);
  return transactions.filter((transaction) => {
    const created = new Date(transaction.createdAt).getTime();
    return transaction.userId === userId && (tab === "detail" || transaction.type === tab) && created >= start && created < end;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function readLocation(route: RecordRoute) {
  const params = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
  const availableTabs: { value: RecordTab; label: string }[] = route === "funds" ? fundsTabs : gameTabs;
  return {
    tab: availableTabs.find((item) => item.value === params.get("tab"))?.value ?? availableTabs[0].value,
    period: periods.find((item) => item.value === params.get("period"))?.value ?? "today" as RecordPeriod,
    notice: params.get("notice") === "submitted" ? "submitted" : undefined,
  };
}

export function RecordPages({ route, profile, onNavigate, onBack, onRefresh, onLogin }: MobilePageProps & { route: RecordRoute }) {
  const [location, setLocation] = useState(() => readLocation(route));
  const [now, setNow] = useState(() => new Date());
  const [visible, setVisible] = useState(pageSize);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const [walletTransfers, setWalletTransfers] = useState<PublicWalletTransfer[] | null>(null);
  const [transferError, setTransferError] = useState("");
  const routeRef = useRef(route);
  routeRef.current = route;
  const refreshOwner = useRef({ mounted: false, version: 0, inFlight: false });
  const refreshAction = useRef(onRefresh);
  const memberId = profile?.user.id;
  const currentMember = useRef(memberId);
  refreshAction.current = onRefresh;
  currentMember.current = memberId;
  const tabButtons = useRef<(HTMLButtonElement | null)[]>([]);
  const tabs: { value: RecordTab; label: string }[] = route === "funds" ? fundsTabs : gameTabs;

  const refreshRecords = useCallback(async () => {
    const owner = refreshOwner.current;
    const member = currentMember.current;
    if (!owner.mounted || !member || owner.inFlight) return;
    const version = ++owner.version;
    const current = () => owner.mounted && owner.version === version && currentMember.current === member;
    owner.inFlight = true;
    setRefreshing(true);
    setRefreshError("");
    try {
      await Promise.all([
        refreshAction.current(),
        routeRef.current === "funds" ? api<{ transfers: PublicWalletTransfer[] }>("/api/user/wallet/transfers", { cache: "no-store", signal: AbortSignal.timeout(15000) })
          .then(data => {
            if (!Array.isArray(data.transfers)) throw new Error("Invalid transfer records");
            if (current()) { setWalletTransfers(data.transfers.filter(item => item.userId === member)); setTransferError(""); }
          })
          .catch(() => { if (current()) setTransferError("场馆转账记录读取失败，请重新查询。已有记录保留为上次查询结果。"); })
          : Promise.resolve(),
      ]);
      if (current()) setNow(new Date());
    } catch {
      if (current()) setRefreshError("记录刷新失败，当前保留上次读取的数据。请重试。");
    } finally {
      if (current()) { owner.inFlight = false; setRefreshing(false); }
    }
  }, []);

  useEffect(() => {
    const owner = refreshOwner.current;
    owner.mounted = true;
    owner.inFlight = false;
    setRefreshing(false);
    setRefreshError("");
    setWalletTransfers(null);
    setTransferError("");
    // Refresh on entry or identity change, not when the refreshed profile object renders again.
    void refreshRecords();
    return () => { owner.mounted = false; owner.version += 1; owner.inFlight = false; };
  }, [route, memberId, refreshRecords]);

  useEffect(() => {
    const sync = () => setLocation(readLocation(route));
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [route]);

  useEffect(() => {
    const update = () => setNow(new Date());
    const timer = window.setInterval(update, 60_000);
    document.addEventListener("visibilitychange", update);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", update); };
  }, []);

  useEffect(() => setVisible(pageSize), [route, location.tab, location.period, profile?.user.id]);

  const activeTab = tabs.some((tab) => tab.value === location.tab) ? location.tab : tabs[0].value;
  const range = recordPeriodRange(location.period, now);
  const records = useMemo(() => filterMemberRecords(profile?.transactions ?? [], profile?.user.id ?? "", activeTab, location.period, now), [profile, activeTab, location.period, now]);
  const transferRecords = useMemo(() => (walletTransfers ?? []).filter(item => {
    const timestamp = Date.parse(item.createdAt);
    return item.userId === memberId && timestamp >= range.start && timestamp < range.end;
  }).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)), [walletTransfers, memberId, range.start, range.end]);
  const activeLabel = tabs.find((tab) => tab.value === activeTab)?.label ?? "记录";
  const changeLocation = (tab: RecordTab, period: RecordPeriod) => {
    setLocation({ tab, period, notice: undefined });
    const params = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
    const filters = route === "game-records" ? Object.fromEntries(["betType", "betStatus", "category"].flatMap(key => params.has(key) ? [[key, params.get(key)!]] : [])) : {};
    onNavigate(route, { ...filters, tab, period });
  };
  const moveTab = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else return;
    event.preventDefault();
    changeLocation(tabs[next].value, location.period);
    tabButtons.current[next]?.focus();
  };
  const isGame = route === "game-records";

  return (
    <section className="mobile-record-page">
      <PageHeader title={isGame ? "游戏管理" : "资金管理"} onBack={onBack} action={profile && <button className="mobile-record-refresh" type="button" aria-label="刷新记录" aria-busy={refreshing} disabled={refreshing} onClick={() => void refreshRecords()}><RefreshCw className={refreshing ? "mobile-spinning" : ""} aria-hidden="true" /><span>{refreshing ? "刷新中" : "刷新"}</span></button>} />
      {!profile ? <div className="mobile-record-login"><RecordEmpty message="登录后查看记录" detail="登录后可查看你的资金申请与游戏记录。" action={<MobilePrimaryButton onClick={onLogin}>登录 / 注册</MobilePrimaryButton>} /></div> : <>
        <div className="mobile-record-content">
          {location.notice === "submitted" && route === "funds" && <p className="mobile-record-submitted" role="status">申请已提交，等待审核。审核通过不代表实际到账。</p>}
          <span className="sr-only" role="status">{refreshing ? "正在刷新记录" : ""}</span>
          {refreshError && <div className="mobile-record-refresh-error" role="alert"><span>{refreshError}</span><button type="button" disabled={refreshing} onClick={() => void refreshRecords()}>重试</button></div>}
          <div className="mobile-record-date">
            <div className="mobile-record-date__label"><strong>查询日期</strong><span title="北京时间">{range.label}</span></div>
            <div className="mobile-record-periods" aria-label="查询日期范围">
              {periods.map((period) => <button key={period.value} type="button" className={period.value === location.period ? "is-active" : ""} aria-pressed={period.value === location.period} onClick={() => changeLocation(activeTab, period.value)}>{period.label}</button>)}
            </div>
          </div>
          <div className="mobile-record-tabs" role="tablist" aria-label={isGame ? "游戏记录分类" : "资金记录分类"}>
            {tabs.map((tab, index) => <button key={tab.value} ref={(element) => { tabButtons.current[index] = element; }} type="button" role="tab" id={`record-tab-${tab.value}`} aria-controls="mobile-record-panel" aria-selected={tab.value === activeTab} tabIndex={tab.value === activeTab ? 0 : -1} className={tab.value === activeTab ? "is-active" : ""} onKeyDown={(event) => moveTab(event, index)} onClick={() => changeLocation(tab.value, location.period)}>{tab.label}</button>)}
          </div>
          <div id="mobile-record-panel" className="mobile-record-panel" role="tabpanel" aria-labelledby={`record-tab-${activeTab}`} tabIndex={0}>
            {activeTab === "profit" || activeTab === "rebate" ? <GameStatistics key={`${memberId}:${activeTab}:${range.start}:${range.end}`} mode={activeTab} userId={memberId!} start={range.start} end={range.end} refreshToken={now.getTime()} onNavigate={onNavigate}/> : activeTab === "games" ? <BetRecordList key={`${memberId}:${range.start}:${range.end}`} userId={memberId!} start={range.start} end={range.end} refreshToken={now.getTime()} onNavigate={onNavigate}/> : activeTab === "transfer" ? <>
              {transferError && <div className="mobile-record-refresh-error" role="alert"><span>{transferError}</span><button type="button" disabled={refreshing} onClick={() => void refreshRecords()}>重试</button></div>}
              {walletTransfers === null ? <div className="mobile-record-empty-slot">{refreshing ? <Spinner label="正在读取转账记录"/> : !transferError && <RecordEmpty message="转账记录尚未读取"/>}</div> : transferRecords.length > 0 ? <>
                <p className="mobile-record-notice">以下为平台钱包与游戏场馆之间的资金划转记录。自动确认中的订单可到钱包查询；标记人工核查的订单请保留单号联系客服。</p>
                <ul className="mobile-record-list" aria-label="转账记录">
                  {transferRecords.slice(0, visible).map(record => <li key={record.id} className="mobile-record-item mobile-wallet-transfer-record">
                    <div className="mobile-record-item__heading"><strong>{record.provider} · {record.direction === "deposit" ? "转入场馆" : "转回平台"}</strong><strong className="mobile-record-item__amount">{currency.format(record.amountCents / 100)}</strong></div>
                    <div className="mobile-record-item__meta"><time dateTime={record.createdAt}>{dateTime.format(new Date(record.createdAt))}</time><span className={`wallet-status wallet-status--${record.status}`}>{walletTransferLabel(record)}</span></div>
                    <TransferSettlementDetails transfer={record}/>
                    <p className="mobile-record-item__note">订单号：{record.id}{record.message && <><br/>{record.message}</>}{isDurableWalletReview(record) && <><br/>该订单已停止自动查询，请联系客服人工核查。</>}</p>
                    {queryableTransfer(record) && <button className="member-text-button" type="button" onClick={() => onNavigate("wallet")}>前往钱包查询原单</button>}
                  </li>)}
                </ul>
                <div className="mobile-record-pagination"><span role="status">已显示 {Math.min(visible, transferRecords.length)} / {transferRecords.length} 条</span>{visible < transferRecords.length && <button type="button" onClick={() => setVisible(count => count + pageSize)}>加载更多</button>}</div>
              </> : !transferError && <div className="mobile-record-empty-slot"><RecordEmpty message="暂无转账记录" detail="当前日期范围内没有场馆转账。"/></div>}
            </> : records.length > 0 ? <>
              <p className="mobile-record-notice">以下为资金申请记录，审核状态不代表实际到账。</p>
              <ul className="mobile-record-list" aria-label={activeLabel}>
                {records.slice(0, visible).map((record) => <li key={record.id} className="mobile-record-item">
                  <div className="mobile-record-item__heading"><strong>{record.type === "deposit" ? "充值申请" : "提现申请"}</strong><strong className="mobile-record-item__amount">{currency.format(record.amount)}</strong></div>
                  <div className="mobile-record-item__meta"><time dateTime={record.createdAt}>{dateTime.format(new Date(record.createdAt))}</time><span className={`mobile-record-status mobile-record-status--${record.status}`}>{record.status === "pending" ? "待审核" : record.status === "approved" ? "审核通过" : "已驳回"}</span></div>
                  {record.note && <p className="mobile-record-item__note">{record.note}</p>}
                </li>)}
              </ul>
              <div className="mobile-record-pagination"><span role="status">已显示 {Math.min(visible, records.length)} / {records.length} 条</span>{visible < records.length && <button type="button" onClick={() => setVisible((count) => count + pageSize)}>加载更多</button>}</div>
            </> : <div className="mobile-record-empty-slot">
              <RecordEmpty message={`暂无${activeTab === "detail" ? "资金申请" : activeLabel.replace("记录", "")}记录`} detail={activeTab === "detail" ? "此处展示充值与提现申请。" : undefined} />
            </div>}
          </div>
        </div>
      </>}
    </section>
  );
}
